import React, { useState, useEffect } from "react";
import { Table, Select, Card, Space, Checkbox, Input } from "antd";
import BiotechDataSummary from "./BiotechDataSummary";

const { Option } = Select;
const { Search } = Input;

const BiotechTable = () => {
    const [data, setData] = useState([]); // Full dataset
    const [filteredData, setFilteredData] = useState([]); // Filtered dataset
    const [selectedPhase, setSelectedPhase] = useState(null);
    const [showOnlyPublic, setShowOnlyPublic] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Fetch data on mount
    useEffect(() => {
        fetch("/biotech_data.json")
            .then((res) => res.json())
            .then((jsonData) => {
                // Transform data: Flatten substances so each row is unique
                const transformedData = jsonData.flatMap((company) =>
                    company.substances.map((substance, index) => ({
                        key: `${company.name.replace(/\s/g, "-")}-${substance.drug_name.replace(/\s/g, "-")}-${index}`, // 💡 Unique key
                        company_name: company.name,
                        orderbook_id: company.orderbook_id || "N/A",
                        marketplace_code: company.marketplace_code || "N/A",
                        market_capital_value: company.market_capital_value || 0,
                        avanza_owners: company.avanza_owners || 0,
                        drug_name: substance.drug_name,
                        phase: substance.phase || "Unknown",
                        therapeutic_area: substance.therapeutic_area || "N/A",
                        indication: substance.indication || "N/A",
                        chemical_modality: substance.chemical_modality || "N/A",
                    }))
                );

                setData(transformedData);
                setFilteredData(transformedData); // Default: Show all data
            })
            .catch((error) => console.error("Error fetching data:", error));
    }, []);

    // Phase Filter
    const handlePhaseChange = (value) => {
        setSelectedPhase(value);
        applyFilters(value, showOnlyPublic);
    };

    // Publicly Traded Filter
    const handlePubliclyTradedChange = (e) => {
        setShowOnlyPublic(e.target.checked);
        applyFilters(selectedPhase, e.target.checked);
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        applyFilters(selectedPhase, showOnlyPublic, value);
    };

    const applyFilters = (phase, onlyPublic, term) => {
        let filtered = [...data];

        if (phase) {
            filtered = filtered.filter((row) => row.phase === phase);
        }

        if (onlyPublic) {
            filtered = filtered.filter((row) => row.orderbook_id !== "N/A");
        }

        if (term) {
            filtered = filtered.filter((row) => row.company_name.toLowerCase().includes(term.toLowerCase()));
        }

        setFilteredData(filtered);
    };

    const uniqueCompanies = new Set(filteredData.map((row) => row.company_name)).size;
    const totalSubstances = filteredData.length;

    const formatMarketCap = (value) => {
        if (!value || value < 1) return "N/A";
        const msekValue = value / 1_000_000; // Always show in MSEK
        return `${msekValue.toLocaleString("sv-SE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MSEK`;
    };

    const columns = [
        {
            title: "Company Name",
            dataIndex: "company_name",
            key: "company_name",
            render: (text, record) =>
                record.orderbook_id !== "N/A" ? (
                    <a
                        href={`https://www.avanza.se/aktier/om-aktien.html/${record.orderbook_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {text}
                    </a>
                ) : (
                    text
                ),
        },
        {
            title: "Mcap (SEK)",
            dataIndex: "market_capital_value",
            key: "market_capital_value",
            sorter: (a, b) => a.market_capital_value - b.market_capital_value, // Sorting enabled
            render: (value) => (
                <span style={{ whiteSpace: "nowrap" }}>{formatMarketCap(value)}</span>
            ),
        },
        // { title: "Orderbook ID", dataIndex: "orderbook_id", key: "orderbook_id" },
        // { title: "Avanza Owners", dataIndex: "avanza_owners", key: "avanza_owners" },
        { title: "Therapeutic Area", dataIndex: "therapeutic_area", key: "therapeutic_area" },
        { title: "Indication", dataIndex: "indication", key: "indication" },
        { title: "Chemical Modality", dataIndex: "chemical_modality", key: "chemical_modality" },
        { title: "Drug Name", dataIndex: "drug_name", key: "drug_name" },
        { title: "Phase", dataIndex: "phase", key: "phase" },

    ];

        return (
            <div style={{ padding: 20 }}>
                <BiotechDataSummary data={data} />

                <Card title="Filters" style={{ marginBottom: 20, textAlign: "left" }}>
                    <Space direction="vertical" size="large" style={{ width: "100%" }}>
                        <Select
                            placeholder="Select Phase"
                            onChange={handlePhaseChange}
                            allowClear
                            style={{ width: 200 }}
                        >
                            <Option value="Discovery">Discovery</Option>
                        <Option value="Preclinical">Preclinical</Option>
                        <Option value="Phase I">Phase 1</Option>
                        <Option value="Phase II">Phase 2</Option>
                        <Option value="Phase III">Phase 3</Option>
                    </Select>

                    <Checkbox onChange={handlePubliclyTradedChange} checked={showOnlyPublic}>
                        Traded Publicly
                    </Checkbox>

                        <Search
                            placeholder="Search by company name"
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                            style={{ width: 300 }}
                        />
                </Space>
            </Card>

            <Card style={{ marginBottom: 20, textAlign: "left", padding: "10px" }}>
                <strong>Matching Results:</strong> {uniqueCompanies} companies, {totalSubstances} substances
            </Card>

            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="key"
                pagination={false} // Show all data
                bordered
            />
        </div>
    );
};

export default BiotechTable;
